import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';

import { convertAmount, dotFormatter, roundNumber } from '../../../../../../helpers';
import { useStore, useTranslation } from '../../../../../../hooks';
import { T } from '../../../../../../constants';

interface DropGiftProps {
  id: number;
  positionX: number;
  positionY: number;
}

export const DropGift: React.FC<DropGiftProps> = observer(({
  id,
  positionX,
  positionY
}) => {

  const t = useTranslation();
  const { freeDropStore, gameStore } = useStore();
  const { currencySymbol, rate, precision } = gameStore;
  const { claimsResponse, claimedPrizes, dropPrizes, dropPassedTime, themeConfig } = freeDropStore;
  const { data: claimResponse, isLoading: claimLoading } = claimsResponse[id] || { isLoading: true };

  const isClaimedRef = useRef(false);
  const fallingElementRef = useRef<HTMLDivElement | null>(null);
  const openingElementRef = useRef<HTMLDivElement | null>(null);

  const [isOpening, setIsOpening] = useState(false);
  const [showPrize, setShowPrize] = useState(false);

  const prizeClaimedOtherUser = claimedPrizes[id] || null;

  useEffect(() => {
    if (!claimLoading) {
      if (claimResponse?.errorMessage) {
        freeDropStore.sounds?.claimError.play();
        const fallingElement = fallingElementRef.current;
        if (fallingElement) {
          setTimeout(() => {
            const fallingAnimations = fallingElement.getAnimations();
            fallingAnimations.forEach((animation) => {
              animation.play();
            });
            setIsOpening(false);
          }, 1000);
        }
      } else {
        isClaimedRef.current = true;
        freeDropStore.sounds?.claimSuccess.play();
        setTimeout(() => {
          setShowPrize(true);
        }, 2000);
      }
    }
  }, [claimLoading]);

  useEffect(() => {
    const openingElement = openingElementRef.current;
    if (openingElement && isOpening) {
      const [openingAnimation] = openingElement.getAnimations();
      let reqId: number;

      if (prizeClaimedOtherUser) {
        openingAnimation.currentTime = 1200;
      }

      const updateTracker = () => {
        const currentTime = openingAnimation.currentTime || 0;

        // repeat bounce animation until response comes
        if (+currentTime > 1000 && !isClaimedRef.current) {
          openingAnimation.currentTime = 500;
        }

        if (openingAnimation.playState !== 'finished') {
          reqId = requestAnimationFrame(updateTracker);
        } else {
          cancelAnimationFrame(reqId);
        }
      }

      reqId = requestAnimationFrame(updateTracker);

      openingAnimation.onfinish = () => {
        openingElement.classList.add('scale-out');
      }

    }
  }, [isOpening]);

  useEffect(() => {
    if (prizeClaimedOtherUser) {
      setIsOpening(true);
      setShowPrize(true);
      isClaimedRef.current = true;
      pauseFalling();
    }
  }, [prizeClaimedOtherUser]);

  const pauseFalling = useCallback(() => {
    const fallingElement = fallingElementRef.current;
    if (fallingElement) {
      const fallingAnimations = fallingElement.getAnimations();
      fallingAnimations.forEach((animation) => {
        animation.pause();
      });
    }
  }, []);

  const handleClick = useCallback(() => {
    if (isOpening) return;
    freeDropStore.claim(id);
    setIsOpening(true);
    pauseFalling();
  }, [isOpening, pauseFalling, id]);

  const claimAmountText = useMemo(() => {
    let win = 0;
    if (claimResponse?.result?.amount) {
      win = roundNumber(claimResponse.result.amount * claimResponse.result.count, precision);
    } else {
      win = convertAmount((prizeClaimedOtherUser?.freeBetCoinAmount * prizeClaimedOtherUser?.freeBetCount), rate, precision)
    }
    return `${dotFormatter(win)}${currencySymbol}`;
  }, [claimResponse, prizeClaimedOtherUser, rate, precision, currencySymbol]);

  const animationDelay = useMemo(() => {
    const dropSceneDuration = freeDropStore.getDropSceneDuration(dropPrizes.length);
    return `${(positionY * dropSceneDuration) - dropPassedTime}ms`;
  }, [dropPrizes, positionY, dropPassedTime]);

  return (
    <div
      onClick={handleClick}
      ref={fallingElementRef}
      className={`open-prize-block-wrapper fall`}
      style={{
        animationDelay,
        left: `${positionX * 80}%`, // todo: better left calculation logic
      }}
    >
      <div
        ref={openingElementRef}
        className={`open-prize-block ${isOpening ? 'active' : ''}`}
        style={{ backgroundImage: `url(${themeConfig.dropSprite})` }}
      >
      </div>
      <div className={`open-prize-info ${showPrize ? 'show-prize' : ''}`}>
        <span className={`open-prize-info-amount ${(prizeClaimedOtherUser && !claimResponse?.result?.amount) ? 'white-color' : ''}`}>
          {claimAmountText}
        </span>
        <span className="open-prize-info-id">
          {
            (prizeClaimedOtherUser && !claimResponse?.result?.amount)
              ? `ID: ${prizeClaimedOtherUser.playerExternalId}`
              : t(T.YOU_WON)
          }
        </span>
      </div>
    </div>
  );
});
